import { ALL_CHART_TYPES, ALL_PRODUCT_TYPES } from "constants/chart.constants";
import { checkSeriesDataYValueGreaterThanMax } from "entities/charts/eva/locks/utils/checkSeriesDataYValueGreaterThanMax";
import { getYMaxProductLock } from "entities/charts/eva/locks/utils/getYMaxProductLock";
import _zip from "lodash/zip";

import { getProportionalPointSize } from "hooks/charts/useSeriesStyle";

import { IChartType } from "models/chart";
import { ChartSeries, IChartResult } from "models/model";

import type { IAxisMinMax } from "components/multiphase-chart/context/ChartSettingsContext";
import { IMPCSeriesLine } from "components/multiphase-chart/models/options.models";
import { IChartPreset } from "components/multiphase-chart/models/shared.models";

import {
  getCurrentSeriesStyleByProductLabel,
  getDefaultSeriesStyleByProductLabel
} from "../productStyles";
import getPerformanceTypeFromLabel from "../productStyles/getPerformanceTypeFromLabel/getPerformanceTypeFromlabel";

export const defaultDataZoom = [
  {
    type: "inside",
    xAxisIndex: [0, 1],
    zoomOnMouseWheel: true,
    filterMode: "none" // Ensures series line goes to the edge of the grid
  },
  {
    type: "inside",
    yAxisIndex: [0, 1],
    zoomOnMouseWheel: true,
    filterMode: "none"
  }
];
export const alignTicks = false;

/**
 * Range for an x-axis of months: opens at month 1 rather than ECharts' 0, and
 * ticks whole months only. The user's locks win.
 */
export const getMonthXAxisRange = ({ xMin, xMax }: IAxisMinMax) => {
  const min = xMin != null ? Number(xMin) : 1;
  return {
    min,
    // A single month would otherwise widen the axis below its min.
    max:
      xMax != null ? Number(xMax) : ({ max }: { max: number }) => Math.max(max, min + 1),
    minInterval: 1
  };
};

// A zoom window's edges fall between months; those get no label.
export const formatMonthAxisLabel = (value: number) =>
  Number.isInteger(value) ? value.toLocaleString("en-US") : "";

export const getChartType = (chartTypeId: number): IChartType => {
  if (!chartTypeId) return null;
  const chartType = Object.values(ALL_CHART_TYPES).find(
    (chartType) => chartType.id === chartTypeId
  );

  if (!chartType) {
    throw "invalid label provided";
  }

  return chartType;
};

export const getDefaultLineSeries = (
  option: IChartPreset,
  data: IChartResult
): IMPCSeriesLine[] => {
  const series: IMPCSeriesLine[] = data.series.map((series) => {
    const seriesData = _zip(series.x, series.y);
    const isSinglePoint = series.y.filter((value) => value != null).length === 1;

    const currentStyle = getCurrentSeriesStyleByProductLabel(series.label, option?.style);

    return {
      type: "line",
      name: series.label,
      data: seriesData,
      xAxisIndex: series.xAxisIndex,
      yAxisIndex: series.yAxisIndex,
      showSymbol: isSinglePoint,
      ...(isSinglePoint && {
        symbolSize: getProportionalPointSize(currentStyle.width)
      }),
      lineStyle: {
        color: currentStyle.color,
        originalColor: currentStyle.color,
        originalWidth: currentStyle.width,
        type: currentStyle.dashed ? "dashed" : "solid",
        width: currentStyle.width
      },
      itemStyle: {
        color: currentStyle.color,
        type: currentStyle.dashed ? "dashed" : "solid",
        width: currentStyle.width
      }
    };
  });

  return series;
};

/**
 * Which product a series is of, as the style helper reads it.
 *
 * A series is named by the service, and that name arrives decorated: "Oil +
 * Cond Historical" is the same product as "Oil + Cond". Matching the raw name
 * against the product labels missed every decorated one, so the ordering below
 * silently did nothing. The style helper already knows how to read past the
 * decoration, and it is asked here rather than the rules being written out
 * again.
 */
const productOf = (name: string): string =>
  getDefaultSeriesStyleByProductLabel(name, getPerformanceTypeFromLabel(name)).product;

/** The order the three main products read in on the legend. */
const LEGEND_PRODUCT_ORDER: string[] = [
  ALL_PRODUCT_TYPES.Oil.label,
  ALL_PRODUCT_TYPES.Gas.label,
  ALL_PRODUCT_TYPES.Water.label
].map(productOf);

/**
 * The names the legend shows, forecasts dropped. Oil, gas and water are put in
 * that order among the places they already hold, so any other product stays
 * where the series left it. Nothing here touches the series: the lines keep
 * their own order, and so do the axes and the tooltip.
 */
export const getLegendData = (series: IMPCSeriesLine[]): string[] => {
  const names = series
    .map((series) => series.name)
    .filter((name) => !name.includes("Forecast"));

  const slots = names.reduce<number[]>((found, name, index) => {
    if (LEGEND_PRODUCT_ORDER.includes(productOf(name))) found.push(index);
    return found;
  }, []);

  const ordered = slots
    .map((slot) => names[slot])
    .sort(
      (a, b) =>
        LEGEND_PRODUCT_ORDER.indexOf(productOf(a)) -
        LEGEND_PRODUCT_ORDER.indexOf(productOf(b))
    );

  slots.forEach((slot, index) => {
    names[slot] = ordered[index];
  });

  return names;
};

/**
 * Opens a ratio's axis at the max the other charts give it (GOR at 20,000 and
 * the like) when its data runs past that, so one spike does not flatten the
 * rest. An axis the user has locked keeps the user's max, and one whose
 * default the user has cleared fits its data.
 */
export const withProductYMaxLocks = <T extends { yAxis?: { max?: number }[] }>(
  options: T,
  data: IChartResult,
  axisMinMax: IAxisMinMax,
  showForecast: boolean
): T => ({
  ...options,
  yAxis: options.yAxis?.map((axis, i) => {
    if (axisMinMax[i]?.yMax != null || axisMinMax[i]?.yMaxReset) return axis;
    // Forecasts arrive whether or not they are shown, as the axis lock input
    // also allows for, and a hidden one must not trigger the lock.
    const axisSeries = data.series.filter(
      (s) => s.yAxisIndex === i && (showForecast || !s.isForecast)
    );
    // Same-unit ratios share an axis (WOR with ISOR/CSOR, WGR with CGR), so
    // each lockable product is checked against its own series only, and the
    // widest cap any of them triggers wins whatever order the series come in.
    const lockable = [
      ...new Set(axisSeries.map((s) => s.label.split("Historical")[0].trim()))
    ].filter((product) => getYMaxProductLock(product));
    const isOf = (product: string) => (s: ChartSeries) =>
      s.label === product || s.label.startsWith(`${product} `);
    const caps = lockable
      .map((product) =>
        checkSeriesDataYValueGreaterThanMax(
          getYMaxProductLock(product),
          axisSeries.filter(isOf(product)),
          product
        )
      )
      .filter(Boolean);
    if (!caps.length) return axis;
    // Never below a ratio with no cap of its own, so only the spiking one clips.
    const uncapped = axisSeries
      .filter((s) => !lockable.some((product) => isOf(product)(s)))
      .flatMap((s) => s.y.filter(Number.isFinite));
    const max = Math.max(...caps, ...uncapped);
    // A cap at or below the user's min would turn the axis upside down.
    const yMin = axisMinMax[i]?.yMin;
    if (yMin != null && Number(yMin) >= max) return axis;
    return { ...axis, max };
  })
});

export const tooltip = {
  trigger: "axis",
  enterable: true,
  confine: true,
  formatter: () => {
    return null;
  },
  axisPointer: {
    type: "cross",
    label: {
      show: true,
      precision: "1"
    }
  },
  showDelay: 500,
  hideDelay: 0
};
